import Backbone from 'backbone'
import _ from 'lodash'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import rowTemplate from './metric-approvals-row.hbs'
import buildMetricTooltip from '../build-metric-tooltip'
import DetailsTooltip from './details-tooltip'
import $ from 'jquery'
import constants from '../../components/constants'
import Breadcrumbs from '../../components/breadcrumbs'
import { shortenedAndUnshortened } from '../../components/format-value'
import DataType from 'scoreboard/datasets/model/data-type'
import i18n from '../../components/i18n'
import ScorecardNode from '../../scorecards/scorecard-node'
import appDisplay from '../../components/app-display'
import NotesDialog from '../../notes/notes-dialog'
import calendars from '../../components/calendars'
import TooltipAndDetails from '../../components/tooltip-and-details'
import { getTextVariation, lookup } from 'scoreboard-colors'
import globals from '../../components/globals'
import { getSeriesKey } from '../../scorecards/equation-value-tooltips'
const { COLORED_CELL_BG_OPACITY } = constants
const { YES_NO } = DataType

const BaseMetricApprovals = Backbone.View.extend(/** @lends {BaseMetricApprovals} */{

	className: 'metric-approvals',

	events: {
		'click .icon-note': 'notes',
	},

	/**
	 * The base view for the metric approvals screen and the preview dialog
	 * (#55590)
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Object} options.data the data to display in the table
	 *
	 * @param {Object} options.seriesBySnId metadata about the series for the
	 * metrics mentioned in the preview
	 */
	initialize: function(options = {}) {
		this.options = options
		this.data = this.options.data
		this.seriesBySnId = this.options.seriesBySnId
		this.detailsTooltips = []
		this.cascadeTooltips = []
	},

	renderMetricsTable: function(rowData) {
		let lastOrg = null
		let lastMetric = null
		const tbody = this.$('tbody')
		if (_.isEmpty(rowData)) {
			this.renderNoResultsRow?.()
		}
		_.forEach(rowData, row => {
			if (!lastOrg || row.organizationId !== lastOrg) {
				const orgRow = `<tr class="table-body-subheader">
					<td colspan="7" class="organization">
						${_.escape(row.organizationLabel)}
					</td>
				</tr>`
				lastOrg = row.organizationId
				tbody.append(orgRow)
			}

			if (!lastMetric || row.scorecardNodeId !== lastMetric) {
				lastMetric = row.scorecardNodeId
				row.newMetric = true
				row.totalVisiblePeriods = _.filter(rowData, r =>
					r.scorecardNodeId === row.scorecardNodeId)
					.length
			}
			let lookupColor = lookup(row.color).color
			let textVariation = getTextVariation(lookupColor, COLORED_CELL_BG_OPACITY)
			row.textVariation = textVariation
			this.renderRow(row, tbody)
			this.$(`[data-metric-id=${row.scorecardNodeId}]`)
				.closest('td')
				.toggleClass('has-multiple-periods',
					row.totalVisiblePeriods && row.totalVisiblePeriods > 1)
		})

		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })
	},

	renderRow: function(row, tbody) {
		const oldValues = this.getFormattedValues(row, u => u.oldValue)
		const newValues = this.getFormattedValues(row, u => u.newValue)
		this.formatOldAndNewValues(row, oldValues, newValues)
		tbody.append(rowTemplate(row))

		// Set the has-notes class on the row if there are notes
		let selector = this.getRowSelector(row) + `svg.icon-note:last-child`
		tbody.find(selector).toggleClass('has-notes', row.hasNotes)

		// build tooltips showing the metric ID and a link to the metric details
		buildMetricTooltip(this.el, '.metric [data-metric-id] .text-link',
			row.scorecardNodeDescription)

		// build tooltip for the Details column
		this.detailsTooltips.push(new DetailsTooltip({
			data: row,
			attachTo: $(this.getRowSelector(row) + `.details svg.icon-info`)
		}))

		// build tooltip for the Cascade icon
		this.cascadeTooltips.push(this.setupCascadeTooltip(row))
	},

	/**
	 * Returns the selector for the row with the given data
	 */
	getRowSelector: function(row) {
		return `tr` +
			`[data-scorecardnodeid=${row.scorecardNodeId}]` +
			`[data-calendarperiodid=${row.calendarPeriodId}] `
	},

	setupCascadeTooltip: function(row) {
		// attach the tooltip to the icon.
		// #61360 use a data attribute to identify the icon that is for showing
		// the cascade tooltip for this metric. This lets us differentiate between
		// any cascade tooltips for any referenced metrics that would be in the
		// same row.
		let selector = this.getRowSelector(row) + `.icon-container[data-type="metric"]`
		return new TooltipAndDetails({
			attachTo: this.$(selector),
			selector,
			tooltipContent: () => {
				let url = `api/${constants.SECTION_SCORECARDS}` +
					`/${row.scorecardNodeId}/ancestors`
				return $.get(url).then(ancestors => {
					return Breadcrumbs.getBreadcrumbs({ objects: ancestors })
				})
			},
			tooltipOptions: {
				extraClass: 'breadcrumbs-tooltip',
				setTooltipBodyMaxHeight: true
			},
			el: this.el
		})
	},

	/**
	 * Builds a list of changed values with the abbreviated and non-abbreviated
	 * versions
	 */
	getFormattedValues(row, getter) {
		return _.map(row.valueUpdates, valueUpdate => {
			let { dataType, currencyCode, precision } = row
			const {labelCode} = valueUpdate
			if (labelCode === 'report.updateMetricsReport.applicable') {
				dataType = YES_NO
			}
			const data = {value: getter(valueUpdate), dataType, currencyCode, precision}
			const values = shortenedAndUnshortened(data)
			values.labelCode = labelCode
			return values
		})
	},

	/**
	 * For each changed value determines if we need to display the unshortened
	 * values or the abbreviated ones and then builds out the HTML to be
	 * displayed
	 */
	formatOldAndNewValues: function(row, oldValues, newValues) {
		for (let i = 0; i < oldValues.length; i++) {
			const oldValue = oldValues[i]
			const newValue = newValues[i]
			const useUnshortened = oldValue.shortened == newValue.shortened
			oldValue.useUnshortened = useUnshortened || oldValue.unshortened == oldValue.shortened
			newValue.useUnshortened = useUnshortened || newValue.unshortened == newValue.shortened
		}

		let self = this
		function format(valueData, index) {
			const label = i18n.get(valueData.labelCode)
			const value = valueData.useUnshortened ? valueData.unshortened : valueData.shortened
			const valueString = _.isNil(value) ? i18n.get('common.blank') : value
			const styleClass = valueData.useUnshortened ? '' : 'is-shortened'
			const type = index == 0 ? 'actual' : 'threshold'
			// the index is meant to be the index of the threshold, not the
			// index of the series. We need to do it this way to support the
			// existing behavior we borrowed from the scorecards overview
			// specified in values.js/values.hbs
			const thresholdIndex = index == 0 ? null : index - 1
			const series = self.seriesBySnId?.[row.scorecardNodeId]?.[index]
			const seriesKey = getSeriesKey(row.scoringTypeId, index)
			// #61480 use the period ID from the row for the header
			let calendarPeriodId = row.calendarPeriodId ?? globals.periodId
			const header = i18n.get(seriesKey) + ': ' +
				calendars.periodsById[calendarPeriodId].label
			const equationHeader =
				i18n.get('scorecards.node.metric.equation.withArg', seriesKey)
			return `` +
				`<span class="value ${styleClass}"
					data-unshortened-value="${_.escape(valueData.unshortened)}"
					data-unshortened-equation="${_.escape(series?.equation || '')}"
					data-unshortened-header="${_.escape(header)}"
					data-unshortened-equation-header="${_.escape(equationHeader)}"
					data-scorecard-node-id="${row.scorecardNodeId}"
					data-type="${type}"
					data-index="${thresholdIndex}"
					data-period-id="${row.calendarPeriodId}"
					data-label="${label}">
					${label}: <strong>${_.escape(valueString)}</strong>
				</span>`
		}

		row.oldValues = _.map(oldValues, format).join('<br>')
		row.newValues = _.map(newValues, format).join('<br>')
	},

	notes: function(e) {
		var row = $(e.target).closest('tr')
		var id = row.data('scorecardnodeid')
		var periodId = row.data('calendarperiodid')
		const model = new ScorecardNode({ id: id })
		const modelPromise = model.fetch({
			data: {
				notes: true,
				calendarPeriodId: periodId
			}
		})
		appDisplay.dialog(modelPromise, () => {
			var notesView = new NotesDialog({
				model,
				defaultTime: periodId,
				timeLabel: calendars.periodsById[periodId].label
			})
			notesView.on('change:notes', (notes) => {
				const hasNotes = _.some(notes, note => !note.periodId ||
					note.periodId == periodId)
				row.find('svg.icon-note').toggleClass('has-notes', hasNotes)
			})
			return notesView
		})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.covertButtonTooltips, this.cascadeTooltip],
			'remove')
		_.invokeMap(this.detailsTooltips, 'remove')
		_.invokeMap(this.cascadeTooltips, 'remove')
	}

})

export default BaseMetricApprovals

BaseMetricApprovals.__filename__ = 'home/metric-approvals/base-metric-approvals.js'
